import { useFieldArray, useFormContext } from 'react-hook-form';

import { Tooltip } from 'primereact/tooltip';
import PropTypes from 'prop-types';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { classNames } from 'primereact/utils';
import DomesticBookDialog from './DomesticBookDialog';

export default function DomesticBooksTable({ control, shouldUpdateFromDocx }) {
  // #region Data
  const { t } = useTranslation();

  const domesticBookRef = useRef();
  const [expandedRows, setExpandedRows] = useState(null);
  const { dirtyFields } = useFormContext();

  const { fields, insert, update, remove } = useFieldArray({
    control,
    name: 'scientificWorks.domesticBooks',
  });
  // #endregion

  // #region Event
  // #endregion

  // #region Render
  const formatIndex = (_, { rowIndex }) => rowIndex + 1;

  const formatRole = (rowData) => (rowData?.role ? t(`contributorRole.${rowData.role}`) : '');

  const formatActions = (_, { rowIndex }) => (
    <div className="flex flex-column float text-center p-0 border-none">
      <i
        onClick={() => {
          domesticBookRef.current.open({
            insert,
            index: rowIndex + 1,
            type: 'INSERT',
          });
        }}
        className="insert-icon pi pi-plus text-primary cursor-pointer my-1"
        data-pr-tooltip={t('formLayout.action.insert')}
        data-pr-position="left"
      />
      <Tooltip target=".insert-icon" />
      <i
        onClick={() => {
          domesticBookRef.current.open({
            update,
            index: rowIndex,
            value: fields[rowIndex],
            type: 'UPDATE',
          });
        }}
        className="edit-icon pi pi-pencil text-primary cursor-pointer mb-1"
        data-pr-tooltip={t('formLayout.action.edit')}
        data-pr-position="left"
      />
      <Tooltip target=".edit-icon" />
      <i
        onClick={() => remove(rowIndex)}
        className="remove-icon pi pi-times text-red-700 cursor-pointer mb-1"
        data-pr-tooltip={t('formLayout.action.remove')}
        data-pr-position="left"
      />
      <Tooltip target=".remove-icon" />
    </div>
  );

  const rowExpansionTemplate = (rowData) => (
    <div className="m-2 text-lg">
      <div className="grid">
        <div className="col-6">
          <strong style={{ color: '#0000ff' }}>
            {t('scientificProfile.scientificWork.book.title')}:{' '}
          </strong>
          {rowData?.book ? rowData.book.title : ''}
        </div>
        <div className="col-6">
          <strong style={{ color: '#0000ff' }}>
            {t('scientificProfile.scientificWork.book.projectCode')}:{' '}
          </strong>
          {rowData?.book ? rowData.book.projectCode : ''}
        </div>
      </div>

      <div className="grid">
        <div className="col-6">
          <strong style={{ color: '#0000ff' }}>
            {t('scientificProfile.scientificWork.book.publisher')}:{' '}
          </strong>
          {rowData?.book ? rowData.book.publisher : ''}
        </div>
        <div className="col-6">
          <strong style={{ color: '#0000ff' }}>
            {t('scientificProfile.scientificWork.book.year')}:{' '}
          </strong>
          {rowData?.book ? rowData.book.year : ''}
        </div>
      </div>

      <div className="grid">
        <div className="col-6">
          <strong style={{ color: '#0000ff' }}>
            {t('scientificProfile.scientificWork.book.authorOrCoauthor')}:{' '}
          </strong>
          {rowData?.role ? t(`contributorRole.${rowData?.role}`) : ''}
        </div>
      </div>

      <div className="grid">
        <div className="col-6">
          <strong style={{ color: '#0000ff' }}>
            {t('scientificProfile.scientificWork.book.pseudonym')}:{' '}
          </strong>
          {rowData?.pseudonym ? rowData.pseudonym : ''}
        </div>
      </div>
    </div>
  );

  // #endregion Render

  return (
    <>
      <DataTable
        value={fields}
        showGridlines
        filterDisplay="menu"
        loading={typeof fields?.length !== 'number'}
        scrollable
        expandedRows={expandedRows}
        onRowToggle={(e) => setExpandedRows(e.data)}
        rowExpansionTemplate={rowExpansionTemplate}
        emptyMessage=" "
        rowClassName={classNames({
          'highlighted-uncertain':
            shouldUpdateFromDocx && dirtyFields.scientificWorks?.domesticBooks,
        })}
      >
        <Column expander style={{ width: '2rem' }} />
        <Column
          header={t('formLayout.numericalOrder')}
          headerClassName="text-center"
          className="text-center"
          body={formatIndex}
          style={{ minWidth: 50, width: 50 }}
          expander
        />
        <Column
          header={t('scientificProfile.scientificWork.book.title')}
          headerClassName="text-center"
          field="book.title"
          style={{ minWidth: 300, width: '33%' }}
        />
        <Column
          header={t('scientificProfile.scientificWork.book.projectCode')}
          headerClassName="text-center"
          field="book.projectCode"
          style={{ minWidth: 180, width: '15%' }}
        />
        <Column
          header={t('scientificProfile.scientificWork.book.publisher')}
          headerClassName="text-center"
          field="book.publisher"
          style={{ minWidth: 170, width: '17%' }}
        />
        <Column
          header={t('scientificProfile.scientificWork.book.year')}
          headerClassName="text-center"
          field="book.year"
          style={{ minWidth: 100, width: '10%' }}
        />
        <Column
          header={t('scientificProfile.scientificWork.book.authorOrCoauthor')}
          headerClassName="text-center"
          body={formatRole}
          style={{ minWidth: 150, width: '15%' }}
        />
        <Column
          header={t('scientificProfile.scientificWork.book.pseudonym')}
          headerClassName="text-center"
          field="pseudonym"
          style={{ minWidth: 150, width: '15%' }}
        />

        <Column
          header={t('formLayout.action.label')}
          headerClassName="text-center"
          style={{ minWidth: 80, width: 80 }}
          body={formatActions}
          frozen
          alignFrozen="right"
        />
      </DataTable>

      {fields?.length === 0 && (
        <div className="flex justify-content-center my-2">
          <i
            onClick={() => {
              domesticBookRef.current.open({
                insert,
                index: 0,
                type: 'INSERT',
              });
            }}
            className="add-domestic-book pi pi-plus text-primary cursor-pointer bg-primary p-1 border-circle"
            data-pr-tooltip={t('formLayout.action.add')}
            data-pr-position="left"
          />
          <Tooltip target=".add-domestic-book" />
        </div>
      )}

      <DomesticBookDialog ref={domesticBookRef} />
    </>
  );
}

DomesticBooksTable.propTypes = {
  control: PropTypes.shape({}).isRequired,
  shouldUpdateFromDocx: PropTypes.bool,
};

DomesticBooksTable.defaultProps = {
  shouldUpdateFromDocx: false,
};
